import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BookOpen, Eye, GripVertical, Plus, X, Save, ArrowLeft, FileText, Sparkles } from 'lucide-react';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { toast } from 'sonner';
import { infantilAPI } from '../services/api';

const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.35 } } };
const FIELD_COLORS = { 'bncc-ei-01': '#8E52AB', 'bncc-ei-02': '#F59E0B', 'bncc-ei-03': '#EC4899', 'bncc-ei-04': '#3B82F6', 'bncc-ei-05': '#10B981' };

export default function PortfolioBuilderPage() {
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedStudent, setSelectedStudent] = useState('');
  const [available, setAvailable] = useState([]);
  const [canvas, setCanvas] = useState([]);
  const [title, setTitle] = useState('');
  const [narrative, setNarrative] = useState('');
  const [saving, setSaving] = useState(false);
  const [ref, setRef] = useState(null);
  const [dragItem, setDragItem] = useState(null);

  useEffect(() => {
    infantilAPI.classes().then(setClasses);
    infantilAPI.reference().then(setRef);
  }, []);
  useEffect(() => { if (selectedClass) infantilAPI.classStudents(selectedClass).then(setStudents); }, [selectedClass]);
  useEffect(() => {
    if (selectedStudent) {
      infantilAPI.observations.list({ student_id: selectedStudent, limit: 100 }).then(d => setAvailable(d.items || []));
    }
  }, [selectedStudent]);

  const addToCanvas = (obs) => {
    if (canvas.find(c => c.id === obs.id)) return;
    setCanvas(prev => [...prev, obs]);
  };
  const removeFromCanvas = (id) => setCanvas(prev => prev.filter(c => c.id !== id));
  const moveItem = (fromIdx, toIdx) => {
    setCanvas(prev => {
      const arr = [...prev];
      const [item] = arr.splice(fromIdx, 1);
      arr.splice(toIdx, 0, item);
      return arr;
    });
  };

  const handleSave = async () => {
    if (!selectedStudent || !selectedClass || canvas.length === 0) { toast.error('Selecione uma criança e adicione evidências'); return; }
    setSaving(true);
    try {
      await infantilAPI.portfolios.create({
        student_id: selectedStudent, class_id: selectedClass,
        title: title || `Portfólio — ${students.find(s => s.id === selectedStudent)?.name || 'Criança'}`,
        narrative, item_ids: canvas.map(c => c.id), status: 'draft',
      });
      toast.success('Portfólio salvo como rascunho!');
      navigate('/v2/infantil/portfolios');
    } catch { toast.error('Erro ao salvar'); }
    finally { setSaving(false); }
  };

  const fieldName = (id) => ref?.bncc_fields?.find(f => f.id === id)?.name || '';
  const availableFiltered = available.filter(a => !canvas.find(c => c.id === a.id));
  const studentName = students.find(s => s.id === selectedStudent)?.name;

  return (
    <motion.div className="space-y-6" initial="hidden" animate="visible" variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.05 } } }}
      data-testid="portfolio-builder">
      {/* Header */}
      <motion.div variants={fadeUp} className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/v2/infantil/portfolios')} className="p-2 rounded-xl hover:bg-slate-100"><ArrowLeft className="h-5 w-5 text-slate-500" /></button>
          <div>
            <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2"><BookOpen className="h-5 w-5 text-[#F59E0B]" /> Builder de Portfólio</h1>
            <p className="text-sm text-slate-500">Monte o portfólio da criança com evidências reais</p>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving || canvas.length === 0} className="rounded-xl" style={{ background: 'linear-gradient(135deg, #2BB673, #1a8f5a)' }}>
          <Save className="h-4 w-4 mr-2" /> {saving ? 'Salvando...' : 'Salvar Rascunho'}
        </Button>
      </motion.div>

      {/* Selectors */}
      <motion.div variants={fadeUp} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs text-slate-600">Turma</Label>
            <select value={selectedClass} onChange={e => { setSelectedClass(e.target.value); setSelectedStudent(''); setCanvas([]); }}
              className="w-full h-10 rounded-xl border border-slate-200 px-3 text-sm">
              <option value="">Selecione...</option>
              {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs text-slate-600">Criança</Label>
            <select value={selectedStudent} onChange={e => { setSelectedStudent(e.target.value); setCanvas([]); }}
              className="w-full h-10 rounded-xl border border-slate-200 px-3 text-sm">
              <option value="">Selecione...</option>
              {students.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs text-slate-600">Título do Portfólio</Label>
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder={studentName ? `Portfólio — ${studentName}` : 'Título...'} className="rounded-xl" />
          </div>
        </div>
      </motion.div>

      {/* 3 Columns */}
      {selectedStudent && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" style={{ minHeight: 400 }}>
          {/* Col 1: Available Evidence */}
          <motion.div variants={fadeUp} className="bg-white rounded-2xl border border-slate-100 shadow-sm flex flex-col">
            <div className="p-4 border-b border-slate-100 flex items-center gap-2">
              <Eye className="h-4 w-4 text-[#64327D]" />
              <h3 className="text-sm font-bold text-slate-800">Evidências Disponíveis</h3>
              <span className="text-[10px] bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full ml-auto">{availableFiltered.length}</span>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-2" style={{ maxHeight: 500 }}>
              {availableFiltered.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-8">Nenhuma evidência disponível</p>
              ) : availableFiltered.map((obs, i) => (
                <div key={obs.id} className="p-3 rounded-xl border border-slate-100 hover:border-[#5B3B8C30] hover:bg-[#5B3B8C04] transition-all cursor-pointer group"
                  onClick={() => addToCanvas(obs)} draggable onDragStart={() => setDragItem(obs)}>
                  <div className="flex items-start gap-2">
                    <div className="w-1 h-10 rounded-full flex-shrink-0" style={{ backgroundColor: FIELD_COLORS[obs.bncc_field_id] || '#94A3B8' }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-slate-700 truncate">{obs.title}</p>
                      <p className="text-[10px] text-slate-500 line-clamp-2">{obs.observation_text?.substring(0, 60)}</p>
                      <div className="flex gap-1 mt-1">
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#DDF7EC] text-[#2BB673]">{obs.context}</span>
                        <span className="text-[9px] text-slate-400">{obs.date}</span>
                      </div>
                    </div>
                    <Plus className="h-4 w-4 text-slate-300 group-hover:text-[#2BB673] flex-shrink-0 transition-colors" />
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Col 2: Canvas */}
          <motion.div variants={fadeUp} className="bg-white rounded-2xl border-2 border-dashed border-slate-200 flex flex-col"
            onDragOver={e => e.preventDefault()}
            onDrop={() => { if (dragItem) { addToCanvas(dragItem); setDragItem(null); } }}>
            <div className="p-4 border-b border-slate-100 flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-[#F59E0B]" />
              <h3 className="text-sm font-bold text-slate-800">Portfólio em Construção</h3>
              <span className="text-[10px] bg-[#F59E0B15] text-[#F59E0B] px-2 py-0.5 rounded-full ml-auto">{canvas.length}</span>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-2" style={{ maxHeight: 500 }}>
              {canvas.length === 0 ? (
                <div className="text-center py-12">
                  <BookOpen className="h-10 w-10 text-slate-200 mx-auto mb-2" />
                  <p className="text-xs text-slate-400">Arraste ou clique nas evidências para adicionar</p>
                </div>
              ) : canvas.map((obs, i) => (
                <div key={obs.id} className="p-3 rounded-xl border border-[#F59E0B30] bg-[#F59E0B04] group">
                  <div className="flex items-start gap-2">
                    <div className="flex flex-col gap-0.5 flex-shrink-0 cursor-grab">
                      <button onClick={() => i > 0 && moveItem(i, i - 1)} className="text-slate-300 hover:text-slate-500 text-[10px]">▲</button>
                      <GripVertical className="h-3 w-3 text-slate-300" />
                      <button onClick={() => i < canvas.length - 1 && moveItem(i, i + 1)} className="text-slate-300 hover:text-slate-500 text-[10px]">▼</button>
                    </div>
                    <div className="w-1 h-10 rounded-full flex-shrink-0" style={{ backgroundColor: FIELD_COLORS[obs.bncc_field_id] || '#94A3B8' }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-slate-700">{obs.title}</p>
                      <p className="text-[10px] text-slate-500 line-clamp-1">{obs.observation_text?.substring(0, 50)}</p>
                    </div>
                    <button onClick={() => removeFromCanvas(obs.id)} className="p-1 rounded-lg hover:bg-red-50 transition-colors flex-shrink-0">
                      <X className="h-3.5 w-3.5 text-slate-300 hover:text-red-500" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Col 3: Preview */}
          <motion.div variants={fadeUp} className="bg-white rounded-2xl border border-slate-100 shadow-sm flex flex-col">
            <div className="p-4 border-b border-slate-100 flex items-center gap-2">
              <FileText className="h-4 w-4 text-[#2BB673]" />
              <h3 className="text-sm font-bold text-slate-800">Prévia Final</h3>
            </div>
            <div className="flex-1 overflow-y-auto p-4" style={{ maxHeight: 500 }}>
              {canvas.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-8">A prévia aparecerá aqui</p>
              ) : (
                <div className="space-y-4">
                  {/* Cover */}
                  <div className="rounded-xl p-5 text-center" style={{ background: 'linear-gradient(135deg, #2E1A47, #64327D)' }}>
                    <p className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Portfólio Pedagógico</p>
                    <p className="text-lg font-bold text-white">{studentName}</p>
                    <p className="text-xs text-white/50 mt-1">1º Trimestre 2026</p>
                  </div>

                  {/* Narrative */}
                  <div>
                    <Label className="text-xs text-slate-600">Narrativa de abertura</Label>
                    <textarea value={narrative} onChange={e => setNarrative(e.target.value)}
                      placeholder="Escreva uma narrativa de abertura sobre o percurso desta criança..."
                      className="w-full h-20 rounded-xl border border-slate-200 px-3 py-2 text-xs resize-none focus:ring-2 focus:ring-[#64327D]/20 mt-1" />
                  </div>

                  {/* Evidence cards */}
                  {canvas.map((obs, i) => (
                    <div key={obs.id} className="p-3 rounded-xl border border-slate-100 bg-slate-50">
                      <p className="text-[10px] text-slate-400 mb-1">{obs.date} — {obs.context}</p>
                      <p className="text-xs font-medium text-slate-700 mb-1">{obs.title}</p>
                      <p className="text-[11px] text-slate-600 leading-relaxed">{obs.observation_text}</p>
                      {obs.child_speech && <p className="text-[11px] italic text-[#64327D] mt-1">"{obs.child_speech}"</p>}
                      {obs.bncc_field_id && <span className="inline-block text-[9px] px-1.5 py-0.5 rounded mt-1" style={{ backgroundColor: (FIELD_COLORS[obs.bncc_field_id] || '#94A3B8') + '15', color: FIELD_COLORS[obs.bncc_field_id] }}>{fieldName(obs.bncc_field_id)}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
}
